<script setup>
import { onBeforeMount, onMounted, onUpdated } from 'vue';
import { getModuleData } from '@/utils/modules';
import { useRouter } from 'vue-router';
import ModuleIconTitle from '@/components/ModuleIconTitle.vue';
import ModuleEmptyCard from '@/components/cards/moduleEmptyCard/ModuleEmptyCard.vue';
import { useBookingResourcesStore } from '@/stores/bookingResources';
import { useCacheStore } from '@/stores/cache';
import ExternalInstitutionTag from '@/components/cards/ExternalInstitutionTag.vue';
import { useInstitutionStore } from '@/stores/institution';

const bookingResourcesStore = useBookingResourcesStore();
const institutionStore = useInstitutionStore();
const cache = useCacheStore();
const router = useRouter();

onBeforeMount(() => {
  cache.use(
    'bookingResourcesStore.getBookingResources',
    bookingResourcesStore.getBookingResources
  );
});

onMounted(() => {
  const content = document.getElementById('content');
  if (content) {
    content.scrollIntoView(true, { behavior: 'instant', block: 'start' });
  }
});

onUpdated(() => {
  const content = document.getElementById('content');
  if (content) {
    content.scrollIntoView(true, { behavior: 'instant', block: 'start' });
  }
});

const resourceSelected = (resource) => {
  router.push(`/bookings/resource/${resource.id}`);
};

const { colorHex, icon, emptyKey, entryRouterName } =
  getModuleData().BOOKING_RESOURCES;
</script>

<template>
  <ModuleEmptyCard
    v-if="!bookingResourcesStore.all.length"
    class="flex h-full"
    :emptyKey="emptyKey"
    :backgroundColor="colorHex"
    iconType="fas"
    :icon="icon"
    :entryRouterName="entryRouterName"
  />

  <div v-else>
    <div class="h-[920px] bg-neutral-100" id="top-spacing" />

    <div
      id="content"
      class="flex flex-col z-10 contain-content min-h-fit bg-neutral-100 items-center pb-10"
    >
      <ModuleIconTitle
        moduleName="BOOKING"
        :backgroundColor="colorHex"
        :icon="icon"
        class="pt-2"
      />

      <div class="min-h-[1320px]">
        <p class="h900 mt-4 text-center">
          {{ $t('booking.moduleHeaderResources') }}
        </p>
        <div class="grid grid-cols-3 gap-10 mt-12">
          <div
            v-for="(resource, index) in bookingResourcesStore.all"
            :key="index"
            data-pressable
            @click="resourceSelected(resource)"
          >
            <div
              class="card-basic relative rounded-2xl flex flex-col items-center justify-center relative p-4 border border-neutral-300 bg-neutral-0"
            >
              <img
                class="mb-4 rounded-full border border-neutral-500 relative"
                v-if="resource.coverImage.source"
                v-optimized="{
                  size: 'small',
                  lazy: true,
                  src: resource.coverImage.source,
                }"
              />

              <ExternalInstitutionTag
                v-if="
                  institutionStore.isExternalInstitution(
                    resource.institution?.id
                  )
                "
                class="absolute bg-neutral-0 bottom-16 border border-neutral-300 rounded-full shadow-smallBottom"
                :title="resource.institution.name"
                :image-source="resource.institution.logo.source"
              />

              <span class="h700 text-center mt-4">
                {{ resource.title }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card-basic {
  width: 270px;
  height: 270px;

  img {
    width: 160px;
    height: 160px;
    object-fit: cover;
  }
}
</style>
